/**
 * Hooks de gestão de membros e convites de um tenant (superadmin).
 *
 * RLS: superadmin tem `for all` em tenant_members, tenant_invitations e profiles
 * (ver migration 20260520000600_superadmin.sql).
 *
 * Papéis disponíveis: gestor | coordenador | supervisor | lider | candidato
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Tipos ────────────────────────────────────────────────────────────────────

export type TenantRole = 'gestor' | 'coordenador' | 'supervisor' | 'lider' | 'candidato';

export const ROLE_LABELS: Record<TenantRole, string> = {
  gestor:       'Gestor',
  coordenador:  'Coordenador',
  supervisor:   'Supervisor',
  lider:        'Líder',
  candidato:    'Candidato',
};

export const ROLE_COLORS: Record<TenantRole, string> = {
  gestor:       'text-amber-300 bg-amber-500/15 border-amber-500/30',
  coordenador:  'text-blue-300 bg-blue-500/15 border-blue-500/30',
  supervisor:   'text-purple-300 bg-purple-500/15 border-purple-500/30',
  lider:        'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
  candidato:    'text-accent bg-accent/15 border-accent/30',
};

export interface TenantMember {
  tenant_id:  string;
  user_id:    string;
  role:       TenantRole;
  active:     boolean;
  joined_at:  string;
  // profile (joined)
  full_name:  string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface TenantInvitation {
  id:          string;
  tenant_id:   string;
  email:       string;
  role:        TenantRole;
  invited_by:  string | null;
  token:       string;
  expires_at:  string;
  consumed_at: string | null;
  created_at:  string;
}

// ── Membros ──────────────────────────────────────────────────────────────────

export function useTenantMembers(tenantId: string | null) {
  return useQuery<TenantMember[]>({
    queryKey: ['superadmin', 'tenant-members', tenantId],
    queryFn: async (): Promise<TenantMember[]> => {
      if (!tenantId) return [];

      // Busca membros
      const { data: members, error } = await db
        .from('tenant_members')
        .select('tenant_id, user_id, role, active, joined_at')
        .eq('tenant_id', tenantId)
        .order('joined_at', { ascending: true });

      if (error) throw error;
      if (!members || members.length === 0) return [];

      // Busca perfis para os user_ids encontrados
      const userIds = (members as { user_id: string }[]).map((m) => m.user_id);
      const { data: profiles } = await db
        .from('profiles')
        .select('id, full_name, display_name, avatar_url')
        .in('id', userIds);

      const profileMap = ((profiles ?? []) as {
        id: string; full_name: string; display_name: string | null; avatar_url: string | null;
      }[]).reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {} as Record<string, { id: string; full_name: string; display_name: string | null; avatar_url: string | null }>);

      return (members as {
        tenant_id: string; user_id: string; role: TenantRole; active: boolean; joined_at: string;
      }[]).map((m) => ({
        ...m,
        full_name:    profileMap[m.user_id]?.full_name    ?? m.user_id.slice(0, 8) + '…',
        display_name: profileMap[m.user_id]?.display_name ?? null,
        avatar_url:   profileMap[m.user_id]?.avatar_url   ?? null,
      }));
    },
    enabled: !!tenantId,
    staleTime: 15_000,
  });
}

// ── Convites ─────────────────────────────────────────────────────────────────

export function useTenantInvitations(tenantId: string | null) {
  return useQuery<TenantInvitation[]>({
    queryKey: ['superadmin', 'tenant-invitations', tenantId],
    queryFn: async (): Promise<TenantInvitation[]> => {
      if (!tenantId) return [];
      const { data, error } = await db
        .from('tenant_invitations')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('consumed_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as TenantInvitation[];
    },
    enabled: !!tenantId,
    staleTime: 15_000,
  });
}

// ── Mutações de membros ───────────────────────────────────────────────────────

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, user_id, role }: { tenant_id: string; user_id: string; role: TenantRole }) => {
      const { error } = await db
        .from('tenant_members')
        .update({ role })
        .eq('tenant_id', tenant_id)
        .eq('user_id', user_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-members', vars.tenant_id] });
    },
  });
}

export function useToggleMemberActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, user_id, active }: { tenant_id: string; user_id: string; active: boolean }) => {
      const { error } = await db
        .from('tenant_members')
        .update({ active })
        .eq('tenant_id', tenant_id)
        .eq('user_id', user_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-members', vars.tenant_id] });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenant_id, user_id }: { tenant_id: string; user_id: string }) => {
      const { error } = await db
        .from('tenant_members')
        .delete()
        .eq('tenant_id', tenant_id)
        .eq('user_id', user_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-members', vars.tenant_id] });
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] }); // atualiza member_count
    },
  });
}

// ── Mutações de convites ──────────────────────────────────────────────────────

export interface CreateInvitationInput {
  tenant_id:  string;
  email:      string;
  role:       TenantRole;
  invited_by: string | null;
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvitationInput) => {
      // Verifica se já existe convite pendente para esse email nesse tenant
      const { data: existing } = await db
        .from('tenant_invitations')
        .select('id')
        .eq('tenant_id', input.tenant_id)
        .eq('email', input.email.trim().toLowerCase())
        .is('consumed_at', null)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (existing) {
        throw new Error('Já existe um convite pendente para esse e-mail.');
      }

      const { error } = await db
        .from('tenant_invitations')
        .insert({
          tenant_id:  input.tenant_id,
          email:      input.email.trim().toLowerCase(),
          role:       input.role,
          invited_by: input.invited_by,
        });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-invitations', vars.tenant_id] });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, tenant_id }: { id: string; tenant_id: string }) => {
      const { error } = await db.from('tenant_invitations').delete().eq('id', id);
      if (error) throw error;
      return { tenant_id };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant-invitations', vars.tenant_id] });
    },
  });
}
